import {
  isInViewport,
  isVisible,
} from './utils.js'

let enableConsoleLogs = false
let logger = (...msg) => enableConsoleLogs && console.log('%cPopupFootnotes:', 'color:green;', ...msg)


/*
  An enhancement for popup footnotes that:
  - returns focus to selected footnote reference when footnote is dismissed
  - quietly dismisses footnote (without changing scroll position) when
    scrolling outside the article's area
*/


/*
  Non-enhanced ways to dismiss a footnote popup:
  - click: targeting popup dismiss link; focus returns to reference
    -> url gets updated with #fndismiss hash
  - popstate: going backwards/forwards in browser history
    -> url gets updated with previous/next entry
 
  Enhancements for dismissing a footnote popup:
  - keyup: escape key
  - click: targeting footnote's own reference link (treating like a toggle)

  Enhancements for hiding a footnote popup:
  - scroll: viewport continues downwards, leaves section containing footnotes;
    popup will show again if footnotes scroll back into view without
    location.hash changing in the meantime

*/


export default class PopupFootnotes {
  constructor(footnotesEl) {
    logger(this)

    this.footnotesEl = footnotesEl
    this.lastSelectedRef = undefined
    this.footnoteIds = []

    for (let fn of this.footnotesEl.querySelectorAll('[id]')) {
      this.footnoteIds.push(fn.id)
      let parentLi = fn.closest('li')
      this.footnoteObserver().observe(parentLi)
    }

    document.addEventListener('click', (event) => {
      if (this.isFootnoteRef(event.target)) {
        logger('heard footnote reference click', event)

        // Keep track of footnote reference:
        this.lastSelectedRef = event.target

        // Handle current return link here as well as in the popstate event listener,
        // as not all triggers for it will register as a popstate event.
        this.handleCurrentReturnLink(`#${this.lastSelectedRef.id}`)

        // While a footnote popup's showing, manage the browser's history to keep only one
        // footnote entry at the top of the stack so that it's always possible to dismiss a
        // footnote popup by going back one step.

        if (this.isFootnoteShowing(event.target.hash)) {
          // The footnote that the event target is pointing to is already showing.
          // Navigate back in the browser's history to dismiss it.
          window.history.back()
        }

        if (this.isFootnoteId(location.hash) && !this.isFootnoteShowing(event.target.hash)) {
          // A footnote is already showing, but it's not the footnote the event target points to.
          // Replace the history's current state instead of pushing on another entry.
          window.history.replaceState(null, '', event.target.hash)
        }
      }
    })

    document.addEventListener('click', (event) => {
      if (event.target.hash && event.target.hash === '#fndismiss') {
        if (this.lastSelectedRef) {
          this.lastSelectedRef.focus()
          this.lastSelectedRef = undefined
        }
      }
    })

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isAnyFootnoteShowing()) {
        window.history.back()
      }
    })

    window.addEventListener('popstate', (event) => {
      this.handleCurrentReturnLink(location.hash)
    })
  }

  isFootnoteId(id) {
    return this.footnoteIds.includes(id.replace('#', ''))
  }

  isFootnoteShowing(id) {
    let cleanedId = id.replace('#', '')
    return location.hash.replace('#', '') === cleanedId && this.isFootnoteId(cleanedId)
  }

  isAnyFootnoteShowing() {
    return location.hash && this.isFootnoteId(location.hash)
  }

  isFootnoteRef(eventTarget) {
    return eventTarget.hash && this.isFootnoteId(eventTarget.hash)
  }

  isFootnoteReturn(hrefValue) {
    return !!hrefValue.match(/^#ref/)
  }

  handleCurrentReturnLink(currentHash) {
    // Only allow a return link to get set as current if:
    // - the footnote it belongs to is the current target of window.location.hash
    // - the last selected reference link's id (this.lastSelectedRef.id)
    //   matches the return link's hash

    let returnLinks = document.querySelectorAll('[href^="#ref"')
    let isFootnoteTarget = this.lastSelectedRef?.hash === currentHash

    for (let link of returnLinks) {
      if (isFootnoteTarget && link.hash === `#${this.lastSelectedRef.id}`) {
        link.setAttribute('aria-current', true)
      } else {
        link.removeAttribute('aria-current')
      }
    }
  }

  footnoteObserver() {
    return new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.setAttribute('data-intersecting', entry.isIntersecting)

          // Only allow the popup to show if the viewport is not below
          // the footnote's normal place in its list:
          if (entry.boundingClientRect.bottom > 0) {
            entry.target.setAttribute('data-show-popup', !entry.isIntersecting)
          }
        })
      },
      {
        // Register intersection as soon as any part of the target is visible
        threshold: 0,
      }
    )
  }


}
